import { Backdrop, Box, Button, Fade, IconButton, Modal, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import ComponentBackdrop from "../../components/backdrop";
import PrivateLayout from "../../layouts/privateLayout";
import BasicBreadcrumbs from "../../components/breadcrumb";
import { createContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Add, Close } from "@mui/icons-material";
import { useApiClient } from "../../context/ApiClientContext";
import api from "../../helper/baseApi";
import toast from "react-hot-toast";
import ErrorAlert from "../../helper/errorAlert";
import Listing from "./listing";
import Form from "./form";
import ConfirmDialog from "../../helper/confirmDialog";
import Teams from "../../data/teamData";
import { GridRowModes } from "@mui/x-data-grid";

export const DataContext = createContext();

function Index() {
    const location = useLocation();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [showButton, setShowButton] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [filteredDataList, setFilteredDataList] = useState([]);
    const [openBackdrop, setOpenBackdrop] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [mode, setMode] = useState('create');
    const [ memberList, setMemberList] = useState([]);
    const [allRows, setAllRows] = useState([]);        // All records from file
    const [rowModesModel, setRowModesModel] = useState({});

    const { apiUrls: { mobileApiUrl } } = useApiClient();
    const navigate = useNavigate();

     const handleAddNewRecord = () => {
        const newId = allRows.length > 0 ? Math.max(...allRows.map(r => r.id)) + 1 : 1;
        const newRow = {
        id: newId,
        no: newId,
        employeeCode: "",
        fullname: "",
        role: "",
        email: "",
        gender: "",
        dob: "",
        designation: "",
        department: "",
        jobgrade: "",
        errors: {},
        isNew: true
        };

        const updatedAllRows = [...allRows, newRow];
        handleDataChange(updatedAllRows);
        setRowModesModel((oldModel) => ({
        ...oldModel,
        [newId]: { mode: GridRowModes.Edit, fieldToFocus: "fullname" },
        }));
    };
    
    const handleDataChange = (rows) => {
        updateAllDataArrays(rows);
        // onDataChange?.(rows);
    };

    const updateAllDataArrays = (rows) => {
        setAllRows(rows);
    };
    const handleOpenConfirm = () => {
        if (selectedIds.length === 0) {
            toast.warning("Please select at least one department to delete.");
            return;
        }
        setOpenConfirm(true);
    };

    const handleCloseConfirm = () => setOpenConfirm(false);

    const handleShowButton = (ids) => {
        setSelectedIds(ids);
        setShowButton(ids.length > 0);
    };

    useEffect(() => {
        // setFilteredDataList(Teams);
        fetchDataAndUpdateList();
        fetchUserList();
    }, [location.pathname]);

    const fetchDataAndUpdateList = async () => {
        try {
            const apiUrl = `${mobileApiUrl}team/listing`;
            const response = await api.get(apiUrl);

            const teamList = response?.data?.data?.teams || [];
            const dataWithKeys = teamList.map((item) => ({
                ...item,
                id: item.teamId
            }));

            setFilteredDataList(dataWithKeys);
        } catch (error) {
            if (error?.response?.status === 404) {
                setFilteredDataList([]);
            } else {
                console.error("An error occurred:", error?.response?.statusText);
            }
        }
    };

    const fetchUserList = async () => {
        try {
        const apiUrl = `${mobileApiUrl}user/user-employee-list`;
        const response = await api.get(apiUrl); 
        const users = response.data.data.userEmployeeList;
        const dataWithKeys = users.map((item) => ({
            id: item.employeeId,
            name: item.name,
            role: item.role
        }));
        
        setMemberList(dataWithKeys);
        } catch (error) {
            if (error?.response?.status === 404) {
                setMemberList([]);
            } else {
                console.error("An error occurred:", error?.response?.statusText);
            }
        }
    };

    const handleConfirmDelete = async () => {
        setOpenBackdrop(true);
        try {
            const apiUrl = `${mobileApiUrl}department/delete`;
            const response = await api.delete(apiUrl, {
                data: { departmentId: selectedIds },
            });

            if (response.status === 200) {
                const deletedItems = filteredDataList.filter(item =>
                    selectedIds.includes(item.id)
                );
                const deletedNames = deletedItems.map(item => item.name);

                toast.success(`Deleted: ${deletedNames.join(", ")}`);

                setFilteredDataList(prev =>
                    prev.filter(item => !selectedIds.includes(item.id))
                );
                setSelectedIds([]);
            } else {
                toast.error("Failed to delete departments. Please try again.");
            }
        } catch (error) {
            console.error("Delete Error:", error);
            ErrorAlert(error, navigate);
        } finally {
            setOpenBackdrop(false);
            setOpenConfirm(false);
        }
    };

    const openModalFn = (data) => {
        setShowModal(true);
        setMode('create');
    };
    const handleClose = () => {
        setShowModal(false);
    };

    const openEditModal = async (id) => {
        try {
            const apiUrl = `${mobileApiUrl}team/view?teamId=${id}`;
            const response = await api.get(apiUrl);
            const details = response.data.data;
            setSelectedTeam(details);
            setShowModal(true);
            setMode('edit');

        } catch (error) {
            ErrorAlert(error, navigate);
        }
    };

    return (
        <PrivateLayout>
            <Box sx={{ mx: "40px", mt: 5 }}>
                <ComponentBackdrop openBackdrop={openBackdrop} />
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        justifyContent: "space-between",
                        alignItems: { xs: "flex-start", sm: "center" },
                        gap: 2,
                    }}
                >
                    <BasicBreadcrumbs first="Teams" />

                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        sx={{
                            width: { xs: "100%", sm: "auto" },
                            alignItems: { xs: "stretch", sm: "flex-start" },
                        }}
                    >
                        {showButton && (
                            <Button
                                variant="contained"
                                size="medium"
                                fullWidth={isMobile}
                                sx={{
                                    backgroundColor: "secondary.main",
                                    fontWeight: "600",
                                    "&:hover": { backgroundColor: "secondary.dark" },
                                    whiteSpace: "nowrap",
                                }}
                                onClick={handleOpenConfirm}
                            >
                                Delete
                            </Button>
                        )}

                        <ConfirmDialog
                            open={openConfirm}
                            onClose={handleCloseConfirm}
                            onConfirm={handleConfirmDelete}
                            title="Delete Teams"
                            message={`Are you sure you want to delete ${selectedIds.length > 1 ? "these teams" : "this teams"
                                }?`}
                            items={filteredDataList
                                .filter(item => selectedIds.includes(item.id))
                                .map(item => item.name)}
                            confirmText="Delete"
                            confirmColor="error"
                        />

                        <Button
                            display="none"
                            variant="contained"
                            size="large"
                            startIcon={<Add />}
                            fullWidth={isMobile}
                            onClick={openModalFn}
                            sx={{
                                backgroundColor: "secondary.main",
                                fontWeight: "600",
                                "&:hover": { backgroundColor: "secondary.dark" },
                                whiteSpace: "nowrap",
                                hidden: "true",
                            }}
                        >
                            Add Team
                        </Button>
                    </Stack>
                </Box>
                <DataContext.Provider value={filteredDataList}>
                    <Listing
                        handleShowButton={handleShowButton}
                        openEditModal={openEditModal}
                    />
                </DataContext.Provider>

                <Modal
                    open={showModal}
                    onClose={handleClose}
                    closeAfterTransition
                    slots={{ backdrop: Backdrop }}
                    slotProps={{ backdrop: { timeout: 500 } }}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Fade in={showModal} timeout={500}>
                        <Box
                            sx={{
                                bgcolor: "background.paper",
                                boxShadow: 24,
                                p: { xs: 2, sm: 3 },
                                borderRadius: 2,
                                position: "relative",
                                overflowY: "auto",
                                width: { xs: "95%", sm: "80%", md: "60%" },
                                maxHeight: "90vh",
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="header"
                                    sx={{

                                        textTransform: "uppercase",
                                    
                                    }}
                                >
                                    {mode === 'create' ? 'Create New Team' : 'Edit Team' }
                                </Typography>
                                <IconButton
                                    aria-label="close"
                                    onClick={handleClose}
                                    sx={{
                                        position: "absolute",
                                        right: 8,
                                        top: 12,
                                        color: (theme) => theme.palette.grey[500],
                                    }}
                                >
                                    <Close />
                                </IconButton>
                            </Box>
                            <Form
                                mode={mode}
                                onModeChange={handleClose}
                                onSuccess={fetchDataAndUpdateList}
                                teamId={mode === 'edit' ? selectedTeam.teamId : 0}
                                teamDetails={mode === 'edit' ? selectedTeam : null}
                                memberList={memberList}
                            />
                        </Box>
                    </Fade>
                </Modal>
            </Box>
        </PrivateLayout>
    );
}

export default Index;